import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { fetchAssignmentList } from "../assignment/assignmentSlice";
import { fetchStudentPaperAssignmentsAPI } from "../paper-assignment/paperAssignmentAPI";
import { fetchLevelTestSummaryAPI } from "../leveltest/levelTestAPI";
import { readSessionFromUrl } from "../leveltest/session";
import { fetchHomeSummary } from "./homeAPI";
import { finishedDateOf, levelTestReviewToItem, sortUnfinished } from "./homeUtils";

// 학생 홈이 한 번에 보는 것: 할 과제, 최근에 끝낸 과제(점수), 알림장, 레벨테스트.
// 새 API 는 없다. 과제 목록 · 알림장 · 레벨테스트 화면이 부르던 것을 그대로 부른다.
const RECENT_FINISHED_LIMIT = 20;
const NOTE_LIMIT = 3;

const useHomeData = (studentId) => {
  const dispatch = useDispatch();
  const [todoList, setTodoList] = useState(null);
  const [finishedList, setFinishedList] = useState([]);
  const [noteList, setNoteList] = useState([]);
  const [levelTest, setLevelTest] = useState(null);
  const [summary, setSummary] = useState(null);

  const loadTodo = useCallback(() => {
    if (!studentId) return Promise.resolve();
    // 홈은 쪽을 나누지 않고 할 과제를 전부 보여 준다. 알림장 화면이 끝낸 과제를 부를 때와 같은 한도다.
    return dispatch(fetchAssignmentList({ studentId, isFinished: false, page: 1, limit: 1000 })).then((result) => {
      setTodoList(result.payload?.results || []);
    });
  }, [dispatch, studentId]);

  useEffect(() => {
    if (!studentId) return undefined;
    let isCurrent = true;

    loadTodo();

    dispatch(fetchAssignmentList({ studentId, isFinished: true, page: 1, limit: RECENT_FINISHED_LIMIT })).then((result) => {
      if (!isCurrent) return;
      const sorted = [...(result.payload?.results || [])].sort(
        (first, second) => new Date(finishedDateOf(second)) - new Date(finishedDateOf(first))
      );
      setFinishedList(sorted);
    });

    fetchStudentPaperAssignmentsAPI(studentId)
      .then((data) => {
        if (!isCurrent) return;
        const sorted = [...(data || [])].sort(
          (first, second) =>
            new Date(second.class_date || second.created_at) - new Date(first.class_date || first.created_at)
        );
        setNoteList(sorted.slice(0, NOTE_LIMIT));
      })
      .catch(() => {});

    fetchHomeSummary()
      .then((data) => {
        if (isCurrent) setSummary(data);
      })
      .catch(() => {});

    const identity = readSessionFromUrl();
    if (identity) {
      fetchLevelTestSummaryAPI(identity.studentId, identity.randomCode)
        .then((data) => {
          if (isCurrent) setLevelTest(data);
        })
        .catch(() => {
          // 레벨테스트를 안 쓰는 학원도 있다. 못 불러오면 그 칸을 그리지 않는다.
        });
    }

    return () => {
      isCurrent = false;
    };
  }, [dispatch, studentId, loadTodo]);

  // 레벨테스트 복습도 해야 할 일이라 과제와 한 줄에 세운다.
  const reviewItemList = (levelTest?.unfinished_reviews || []).map(levelTestReviewToItem);
  const sortedTodoList = todoList === null ? null : sortUnfinished([...todoList, ...reviewItemList]);

  return { todoList: sortedTodoList, finishedList, noteList, levelTest, summary, reloadTodo: loadTodo };
};

export default useHomeData;
